import { Helmet } from "react-helmet-async";
import { switchLangInPath, detectLangFromPath, type Lang } from "@/lib/url-slugs";

interface SEOProps {
  title: string;
  description?: string;
  /** Path-only (e.g. "/lv/p/foo") OR full URL. Used for canonical. */
  canonical?: string;
  image?: string;
  /** "index,follow" by default. Pass "noindex,follow" etc. to override. */
  robots?: string;
  type?: "website" | "article" | "product";
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
}

const SITE_ORIGIN = "https://padelcorner.lv";

const toAbsolute = (urlOrPath?: string) => {
  if (!urlOrPath) return undefined;
  if (/^https?:\/\//i.test(urlOrPath)) return urlOrPath;
  return `${SITE_ORIGIN}${urlOrPath.startsWith("/") ? "" : "/"}${urlOrPath}`;
};

const pathOnly = (urlOrPath: string): string => {
  if (/^https?:\/\//i.test(urlOrPath)) {
    try {
      const u = new URL(urlOrPath);
      return u.pathname + u.search;
    } catch {
      return urlOrPath;
    }
  }
  return urlOrPath;
};

export default function SEO({
  title,
  description,
  canonical,
  image,
  robots = "index,follow",
  type = "website",
  jsonLd,
}: SEOProps) {
  const rawPath =
    canonical ||
    (typeof window !== "undefined" ? window.location.pathname + window.location.search : "/");
  const canonicalPath = pathOnly(rawPath);
  const canonicalAbs = toAbsolute(canonicalPath) || SITE_ORIGIN;

  // hreflang alternates
  const currentLang: Lang = detectLangFromPath(canonicalPath) || "lv";
  const altLang: Lang = currentLang === "lv" ? "en" : "lv";
  const lvPath = currentLang === "lv" ? canonicalPath : switchLangInPath(canonicalPath, "lv");
  const enPath = currentLang === "en" ? canonicalPath : switchLangInPath(canonicalPath, "en");

  const imageAbs = toAbsolute(image) || `${SITE_ORIGIN}/logo.png`;
  const fullTitle = title.length > 60 ? title.slice(0, 57) + "…" : title;
  const ogLocale = currentLang === "en" ? "en_GB" : "lv_LV";
  const ogLocaleAlt = currentLang === "en" ? "lv_LV" : "en_GB";

  return (
    <Helmet>
      <html lang={currentLang} />
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description.slice(0, 160)} />}
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonicalAbs} />
      <link rel="alternate" hrefLang="lv" href={`${SITE_ORIGIN}${lvPath}`} />
      <link rel="alternate" hrefLang="en" href={`${SITE_ORIGIN}${enPath}`} />
      <link rel="alternate" hrefLang="x-default" href={`${SITE_ORIGIN}${lvPath}`} />

      <meta property="og:type" content={type === "product" ? "product" : type} />
      <meta property="og:site_name" content="PADELCORNER.LV" />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={ogLocaleAlt} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description.slice(0, 160)} />}
      <meta property="og:url" content={canonicalAbs} />
      <meta property="og:image" content={imageAbs} />
      <meta property="og:image:alt" content={fullTitle} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@padelcorner" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description.slice(0, 160)} />}
      <meta name="twitter:image" content={imageAbs} />

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}
